import { Box, Spinner, useToast } from "@chakra-ui/react";
import React, {
  createContext,
  Fragment,
  useContext,
  useEffect,
  useRef,
} from "react";
import {
  CompanyBootstrap,
  cookies,
  useCompanyBootstrap,
  useSession,
  withTokenParam,
} from "../../shared/session";
import { ChatsContext } from "../../context/ChatContext";

interface Props {
  children: React.ReactNode;
}

// The company bootstrap runs once here (one getUserCompany per token per page
// through the shared request cache, skipped when the company cookies are
// already set and this isn't a fresh login); CheckInformation reads the result
// to decide where "/" goes.
const CompanyContext = createContext<CompanyBootstrap | null>(null);

export const useCompany = (): CompanyBootstrap => {
  const value = useContext(CompanyContext);
  if (!value) throw new Error("useCompany() must be used under <CompanyWrapper>");
  return value;
};

const hasCompanyCookies = () =>
  !!cookies.getCompanyId() && !!cookies.getCompanyName();

const CompanyWrapper: React.FC<Props> = ({ children }) => {
  const bootstrap = useCompanyBootstrap();
  const { token } = useSession();
  const { companyID, setCompanyID, setCompanyName } = useContext(ChatsContext);
  const toast = useToast();
  // pages that already render with the company cookies stay mounted while a
  // fresh login re-checks the company (they remount only if it changed)
  const [hadCompany] = React.useState(hasCompanyCookies);
  const redirected = useRef(false);

  // the chat context (company-scoped chats, websocket headers) follows the company
  useEffect(() => {
    const company = bootstrap.company;
    if (bootstrap.status !== "ready" || !company) return;
    const id = `${company.companyID}`;
    if (id !== companyID) setCompanyID(id);
    if (company.name !== undefined && company.name !== null)
      setCompanyName(`${company.name}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootstrap.status, bootstrap.company]);

  // no company yet: company creation lives in the account app
  useEffect(() => {
    if (bootstrap.status !== "noCompany" || redirected.current || !token) return;
    redirected.current = true;
    localStorage.removeItem("from");
    toast({
      title: "No company found, Please create company",
      status: "success",
      isClosable: true,
      position: "top",
    });
    window.open(
      withTokenParam(`${process.env.REACT_APP_ACCOUNT_APP}`, token),
      "_self"
    );
  }, [bootstrap.status, token, toast]);

  if (
    (bootstrap.status === "loading" && !hadCompany) ||
    bootstrap.status === "noCompany"
  ) {
    return (
      <Box h="100vh" display="flex" alignItems="center" justifyContent="center">
        <Spinner />
      </Box>
    );
  }

  return (
    <CompanyContext.Provider value={bootstrap}>
      {/* company-scoped pages start over when a fresh login switched company */}
      <Fragment key={bootstrap.companyChanged ? bootstrap.company?.companyID : undefined}>
        {children}
      </Fragment>
    </CompanyContext.Provider>
  );
};

export default CompanyWrapper;
